// src/pages/financeOfficer/FinanceProcurementDashboard.jsx

"use client";

import React, { useState, useEffect, useMemo, Fragment } from "react";
import { 
  Eye, CheckCircle, MoreHorizontal, DollarSign, 
  Search, Filter, CheckSquare 
} from "lucide-react";
import { toast } from "react-toastify";
import Select from "react-select";
import { Dialog, Transition } from "@headlessui/react";
import TopBar from "../../components/TopbarNav";
import FOSidebar from "../../components/MasterSideBar";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumCard from "../../components/common/PremiumCard";
import { getRequisitions, updateRequisition } from "../../api/procurementApi";
import ViewRequisitionModal, { ViewRequisitionContent } from '../../components/programManager/ViewRequisitionModal';

// --- Constants for Finance Officer View ---
const REQUISITION_STATUS_OPTIONS = [
    { value: "all", label: "All Requisitions" },
    { value: "approved_by_director", label: "Approved by Director (Awaiting Payment)" },
    { value: "pending_executive_approval", label: "Pending Executive Approval" },
    { value: "processing_by_procurement", label: "Payment in Progress" },
    { value: "completed", label: "Completed (Paid)" },
    { value: "on_hold", label: "On Hold (Finance)" },
];

const FinanceProcurementDashboard = () => {
    const [requisitions, setRequisitions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(REQUISITION_STATUS_OPTIONS[0]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [requisitionToView, setRequisitionToView] = useState(null);

    const [showProcessModal, setShowProcessModal] = useState(false);
    const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);
    const [currentRequisitionForAction, setCurrentRequisitionForAction] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [paymentRef, setPaymentRef] = useState('');
    const [requestComments, setRequestComments] = useState('');

    const fetchRequisitions = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getRequisitions({
                status: 'approved_by_director,pending_executive_approval,processing_by_procurement,completed,on_hold'
            });
            if (data && Array.isArray(data.data)) {
                setRequisitions(data.data);
            } else if (Array.isArray(data)) {
                setRequisitions(data);
            } else {
                console.error("API response is not a valid array:", data);
                setRequisitions([]);
            }
        } catch (err) {
            console.error("Failed to fetch requisitions for Finance:", err);
            setError("Failed to load requisitions. Ensure you are logged in as a Finance Officer.");
            toast.error("Failed to load requisitions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequisitions();
    }, []);

    const filteredRequisitions = useMemo(() => {
        if (!requisitions) return [];
        const query = searchQuery.toLowerCase();

        return requisitions.filter(req => {
            const matchesStatus = selectedStatusFilter.value === 'all' || req.currentStatus === selectedStatusFilter.value;
            const matchesSearch = req.title.toLowerCase().includes(query) ||
                (req.projectId?.projectTitle || '').toLowerCase().includes(query) ||
                (req.requester?.firstName || '').toLowerCase().includes(query) ||
                (req.requester?.lastName || '').toLowerCase().includes(query);
            return matchesStatus && matchesSearch;
        });
    }, [requisitions, selectedStatusFilter, searchQuery]);

    const handleProcessPayment = (requisition) => {
        setCurrentRequisitionForAction(requisition);
        setPaymentAmount(requisition.estimatedCost || '');
        setPaymentDate(new Date().toISOString().slice(0, 10));
        setPaymentRef('');
        setShowProcessModal(true);
    };

    const handleRequestMoreInfo = (requisition) => {
        setCurrentRequisitionForAction(requisition);
        setShowRequestInfoModal(true);
    };

    const handleMarkAsPaid = async (requisition) => {
        if (window.confirm(`Are you sure you want to mark requisition '${requisition.title}' as paid? This action is final.`)) {
            setLoading(true);
            try {
                const payload = { currentStatus: 'completed' };
                await updateRequisition(requisition._id, payload);
                toast.success(`Requisition '${requisition.title}' has been marked as completed.`);
                fetchRequisitions();
            } catch (err) {
                console.error("Error marking requisition as paid:", err);
                toast.error("Failed to mark as paid.");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleViewRequisitionDetails = (requisition) => {
        setRequisitionToView(requisition);
        setIsViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setRequisitionToView(null);
        setIsViewModalOpen(false);
    };

    const handleSubmitPaymentDetails = async () => {
        if (!currentRequisitionForAction) return;
        setLoading(true);
        try {
            const payload = {
                currentStatus: 'processing_by_procurement',
                paymentDetails: {
                    amount: paymentAmount,
                    date: paymentDate,
                    reference: paymentRef,
                },
            };
            await updateRequisition(currentRequisitionForAction._id, payload);
            toast.success("Payment details submitted successfully!");
            setShowProcessModal(false);
            fetchRequisitions();
        } catch (err) {
            console.error("Error processing payment:", err);
            toast.error("Failed to process payment details.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitRequestInfo = async () => {
        if (!currentRequisitionForAction || !requestComments) {
            toast.error("Please provide comments for the request.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                currentStatus: 'on_hold',
                financeComments: requestComments,
            };
            await updateRequisition(currentRequisitionForAction._id, payload);
            toast.success("Request for more information sent successfully!");
            setShowRequestInfoModal(false);
            fetchRequisitions();
        } catch (err) {
            console.error("Error requesting more info:", err);
            toast.error("Failed to send request.");
        } finally {
            setLoading(false);
        }
    };

    const selectStyles = {
        control: (base) => ({
            ...base,
            borderRadius: '0.75rem',
            padding: '0.25rem',
            border: '1px solid #f3f4f6',
            fontSize: '0.75rem',
            fontWeight: '700',
            boxShadow: 'none',
            '&:hover': { border: '1px solid #e5e7eb' }
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '0.75rem',
            fontWeight: '700',
            backgroundColor: state.isSelected ? '#111827' : state.isFocused ? '#f9fafb' : 'transparent',
            color: state.isSelected ? 'white' : '#374151',
        })
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <FOSidebar
                visible={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
            />

            <div className={`flex flex-col flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 overflow-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
                            <div className="flex-1">
                                <PremiumHeader 
                                    title="Procurement" 
                                    lastWordAccent="Settlements" 
                                    subtitle="Verify project requisitions, manage disbursements, and track payment completions." 
                                />
                            </div>
                        </div>

                        <PremiumCard icon={<Filter size={18} />} title="Settlement Queue Filters">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
                                <div className="space-y-2">
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Lifecycle Status</p>
                                    <Select
                                        options={REQUISITION_STATUS_OPTIONS}
                                        value={selectedStatusFilter}
                                        onChange={setSelectedStatusFilter}
                                        styles={selectStyles}
                                        isClearable={false}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Universal Search</p>
                                    <div className="relative">
                                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                        <input 
                                            type="text" 
                                            placeholder="Search by title, project, or requester..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full bg-white border border-gray-100 rounded-xl pl-12 pr-4 py-3 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-gray-900 focus:outline-none transition-all shadow-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                        </PremiumCard>

                        <div className="mt-10">
                            <PremiumCard icon={<CheckSquare size={18} />} title="Requisition Ledger">
                                {loading ? (
                                    <div className="flex justify-center items-center py-20">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                                    </div>
                                ) : error ? (
                                    <div className="text-center py-20">
                                        <p className="text-xs font-black text-red-500 uppercase tracking-widest">{error}</p>
                                    </div>
                                ) : filteredRequisitions.length === 0 ? (
                                    <div className="text-center py-20">
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">No requisitions match your current filters</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-100 text-left">
                                            <thead>
                                                <tr className="bg-gray-50/50">
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Requisition Title</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Stakeholders</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Valuation</th>
                                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                                                    <th className="px-6 py-4 text-right"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {filteredRequisitions.map((req) => (
                                                    <tr key={req._id} className="hover:bg-gray-50/50 transition-colors">
                                                        <td className="px-6 py-5">
                                                            <p className="text-xs font-black text-gray-800 uppercase tracking-tighter">{req.title}</p>
                                                            <p className="text-[9px] font-black text-gray-400 uppercase mt-0.5">{req.projectId?.projectTitle || 'General Overhead'}</p>
                                                        </td>
                                                        <td className="px-6 py-5 whitespace-nowrap">
                                                            <div className="flex items-center gap-2">
                                                                <div className="size-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-black text-gray-500">
                                                                    {req.requester?.firstName?.charAt(0)}{req.requester?.lastName?.charAt(0)}
                                                                </div>
                                                                <p className="text-[10px] font-bold text-gray-600">{`${req.requester?.firstName || ''} ${req.requester?.lastName || ''}`}</p>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-5 whitespace-nowrap font-black text-xs text-gray-700">
                                                            UGX {req.estimatedCost ? req.estimatedCost.toLocaleString() : '0'}
                                                        </td>
                                                        <td className="px-6 py-5 whitespace-nowrap">
                                                            <span className={`px-2.5 py-1 text-[9px] font-black uppercase tracking-tighter rounded-lg
                                                                ${req.currentStatus === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                                                                    req.currentStatus === 'approved_by_director' ? 'bg-blue-50 text-blue-600' :
                                                                        req.currentStatus === 'processing_by_procurement' ? 'bg-orange-50 text-orange-600' :
                                                                            req.currentStatus === 'on_hold' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                {REQUISITION_STATUS_OPTIONS.find(opt => opt.value === req.currentStatus)?.label.replace('(Awaiting Payment)', '').replace('(Finance)', '').replace('(Paid)', '') || req.currentStatus.replace(/_/g, ' ')}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-5 text-right space-x-3">
                                                            <button
                                                                onClick={() => handleViewRequisitionDetails(req)}
                                                                className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors"
                                                                title="View Details"
                                                            >
                                                                <Eye size={16} />
                                                            </button>
                                                            {req.currentStatus === 'approved_by_director' && (
                                                                <button
                                                                    onClick={() => handleProcessPayment(req)}
                                                                    className="p-1.5 text-emerald-500 hover:text-emerald-700 transition-colors"
                                                                    title="Disburse Funds"
                                                                >
                                                                    <DollarSign size={16} />
                                                                </button>
                                                            )}
                                                            {req.currentStatus === 'processing_by_procurement' && (
                                                                <button
                                                                    onClick={() => handleMarkAsPaid(req)}
                                                                    className="p-1.5 text-blue-500 hover:text-blue-700 transition-colors"
                                                                    title="Close Settlement"
                                                                >
                                                                    <CheckCircle size={16} />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleRequestMoreInfo(req)}
                                                                className="p-1.5 text-amber-500 hover:text-amber-700 transition-colors"
                                                                title="Info Request"
                                                            >
                                                                <MoreHorizontal size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </PremiumCard>
                        </div>
                    </div>
                </main>
            </div>

            {/* Modals */}
            <Transition appear show={isViewModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={handleCloseViewModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-[2.5rem] bg-white p-8 text-left align-middle shadow-2xl transition-all border border-gray-100">
                                    <ViewRequisitionContent
                                        requisition={requisitionToView}
                                        onRequestClose={handleCloseViewModal}
                                    />
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Process Payment Modal */}
            <Transition appear show={showProcessModal} as={Fragment}>
                <Dialog as="div" className="relative z-[100]" onClose={() => setShowProcessModal(false)}>
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-[2.5rem] bg-white p-10 text-left align-middle shadow-2xl transition-all border border-gray-100">
                                    <div className="mb-8 text-center">
                                        <div className="size-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <DollarSign className="text-emerald-600" size={32} />
                                        </div>
                                        <Dialog.Title as="h3" className="text-xs font-black text-gray-800 uppercase tracking-[0.2em]">Disburse Funds</Dialog.Title>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase mt-2">Finalize payment details for this requisition</p>
                                    </div>
                                    
                                    <div className="space-y-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Payment Amount (UGX)</label>
                                            <input
                                                type="number"
                                                value={paymentAmount}
                                                onChange={(e) => setPaymentAmount(e.target.value)}
                                                className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 text-sm font-black text-gray-800 focus:ring-2 focus:ring-emerald-500 outline-none transition-all shadow-inner"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Value Date</label>
                                            <input
                                                type="date"
                                                value={paymentDate}
                                                onChange={(e) => setPaymentDate(e.target.value)}
                                                className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 text-sm font-black text-gray-800 focus:ring-2 focus:ring-emerald-500 outline-none transition-all shadow-inner"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Transaction Reference</label>
                                            <input
                                                type="text"
                                                value={paymentRef}
                                                placeholder="e.g. EFT-1234567"
                                                onChange={(e) => setPaymentRef(e.target.value)}
                                                className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 text-sm font-black text-gray-800 focus:ring-2 focus:ring-emerald-500 outline-none transition-all shadow-inner placeholder:text-gray-300"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-10 flex gap-4">
                                        <button
                                            type="button"
                                            className="flex-1 bg-gray-50 text-gray-400 px-6 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-100 hover:text-gray-800 transition-all active:scale-95"
                                            onClick={() => setShowProcessModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="flex-1 bg-gray-900 text-white px-6 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-emerald-600 transition-all active:scale-95 shadow-xl shadow-gray-200"
                                            onClick={handleSubmitPaymentDetails}
                                        >
                                            Disburse
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Request More Info Modal */}
            <Transition appear show={showRequestInfoModal} as={Fragment}>
                <Dialog as="div" className="relative z-[100]" onClose={() => setShowRequestInfoModal(false)}>
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-[2.5rem] bg-white p-10 text-left align-middle shadow-2xl transition-all border border-gray-100">
                                    <div className="mb-8 text-center">
                                        <div className="size-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <MoreHorizontal className="text-amber-600" size={32} />
                                        </div>
                                        <Dialog.Title as="h3" className="text-xs font-black text-gray-800 uppercase tracking-[0.2em]">Clarification Request</Dialog.Title>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase mt-2">Request additional information from requester</p>
                                    </div>
                                    <div className="mt-4">
                                        <textarea
                                            value={requestComments}
                                            onChange={(e) => setRequestComments(e.target.value)}
                                            rows="5"
                                            className="w-full bg-gray-50 border-none rounded-2xl p-5 text-sm font-bold text-gray-700 focus:ring-2 focus:ring-amber-500 outline-none transition-all shadow-inner placeholder:text-gray-300"
                                            placeholder="Please specify what documentation or justification is missing for this settlement..."
                                        ></textarea>
                                    </div>
                                    <div className="mt-8 flex gap-4">
                                        <button
                                            type="button"
                                            className="flex-1 bg-gray-50 text-gray-400 px-6 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-100 hover:text-gray-800 transition-all active:scale-95"
                                            onClick={() => setShowRequestInfoModal(false)}
                                        >
                                            Dismiss
                                        </button>
                                        <button
                                            type="button"
                                            className="flex-1 bg-gray-900 text-white px-6 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-amber-600 transition-all active:scale-95 shadow-xl shadow-gray-200"
                                            onClick={handleSubmitRequestInfo}
                                        >
                                            Send Request
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

export default FinanceProcurementDashboard;